import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter, ArrowUpDown, Clock, ExternalLink, FileText, User, Calendar, Briefcase, Building, ChevronDown, ChevronRight, Loader2, File, UserRound, Signature } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/SearchDeeper.css';
import Modal from './Modal';
import { useNavVariant } from '../App';

export const SearchDeeper = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const navVariant = useNavVariant();
    
    // Get initial search term from URL params or previous search
    const params = new URLSearchParams(location.search);
    const initialSearchTerm = params.get('q') || '';
    
    const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
    const [showFilters, setShowFilters] = useState(false);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [searchStats, setSearchStats] = useState(null);
    
    // Filter states
    const [filters, setFilters] = useState({
        sortBy: 'relevance', // 'relevance' or 'activity'
        filterByPrimary: 'all', // 'all' or specific user ID
        includeArchived: false,
        openInNewTab: true,
        searchTypes: {
            cases: true,
            leads: true,
            people: true,
            docs: true,
            calendar: true,
            feedUpdates: true,
            fieldUpdates: true
        }
    });

    // User list for first primary filter
    const [primaryUsers, setPrimaryUsers] = useState([]);

    // Modal state for file actions
    const [showFileModal, setShowFileModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    // Refs
    const searchInputRef = useRef(null);
    const searchTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);

    // Focus search input on mount and cleanup on unmount
    useEffect(() => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }

        // Fetch list of users for primary filter
        fetchPrimaryUsers();

        // Cleanup function to abort any pending requests when component unmounts
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    // Fetch primary users for filter dropdown
    const fetchPrimaryUsers = async () => {
        try {
            const response = await fetch('https://api.casedb.co/user.php?attorney=true', {
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success && data.users) {
                setPrimaryUsers(data.users);
            }
        } catch (error) {
            console.error('Error fetching attorneys:', error);
        }
    };

    // Perform search when term changes (with debounce)
    useEffect(() => {
        document.title = searchTerm ? `${searchTerm} | CaseDB` : "Search Deeper | CaseDB";
        
        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Abort any ongoing request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        if (searchTerm.trim()) {
            searchTimeoutRef.current = setTimeout(() => {
                performSearch();
            }, 300); // 300ms debounce
        } else {
            setResults([]);
            setSearchStats(null);
            setLoading(false);
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        };
    }, [searchTerm, filters]);

    // Update URL when search term changes
    useEffect(() => {
        if (searchTerm) {
            const newParams = new URLSearchParams(location.search);
            newParams.set('q', searchTerm);
            const newUrl = `${location.pathname}?${newParams.toString()}`;
            window.history.replaceState({}, '', newUrl);
        }
    }, [searchTerm, location.pathname, location.search]);

    const performSearch = async () => {
        if (!searchTerm.trim()) return;

        // Abort any existing request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new AbortController for this request
        abortControllerRef.current = new AbortController();
        
        setLoading(true);
        
        try {
            const searchParams = new URLSearchParams({
                q: searchTerm,
                sort_by: filters.sortBy,
                filter_by_primary: filters.filterByPrimary,
                include_archived: filters.includeArchived,
                search_types: JSON.stringify(filters.searchTypes)
            });

            console.log('Starting search for:', searchTerm);

            const response = await fetch(`https://api.casedb.co/search-deeper.php?${searchParams}`, {
                credentials: 'include',
                signal: abortControllerRef.current.signal
            });

            // Check if request was aborted
            if (abortControllerRef.current.signal.aborted) {
                console.log('🚫 Search request was aborted after fetch');
                return;
            }

            const data = await response.json();

            if (data.success) {
                setResults(data.results || []);
                setSearchStats(data.stats || null);
            } else {
                console.error('Search failed:', data.message);
                setResults([]);
                setSearchStats(null);
            }
        } catch (error) {
            // Don't show errors for aborted requests
            if (error.name === 'AbortError') {
                console.log('🚫 Search request was cancelled:', searchTerm);
                return;
            }
            
            console.error('Search error for:', searchTerm, error);
            setResults([]);
            setSearchStats(null);
        } finally {
            // Only update loading state if this request wasn't aborted
            if (!abortControllerRef.current?.signal.aborted) {
                setLoading(false);
                abortControllerRef.current = null;
            }
        }
    };

    const handleFilterChange = (filterKey, value) => {
        if (filterKey.includes('.')) {
            // Handle nested filter keys like searchTypes.cases
            const [parent, child] = filterKey.split('.');
            setFilters(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFilters(prev => ({
                ...prev,
                [filterKey]: value
            }));
        }
    };

    const handleResultClick = (result) => {
        // Special handling for documents/files
        if (result.type === 'document') {
            setSelectedFile(result);
            setShowFileModal(true);
            return;
        }

        // Standard navigation for other result types
        const url = getResultUrl(result);
        if (url) {
            if (filters.openInNewTab) {
                window.open(url, '_blank');
            } else {
                navigate(url);
            }
        }
    };

    const handleFileAction = (action) => {
        if (!selectedFile) return;

        let url;
        if (action === 'file') {
            url = `/file/${selectedFile.id}`;
        } else if (action === 'case' && selectedFile.case_id) {
            url = `/case/${selectedFile.case_id}`;
        }

        if (url) {
            if (filters.openInNewTab) {
                window.open(url, '_blank');
            } else {
                navigate(url);
            }
        }

        setShowFileModal(false);
        setSelectedFile(null);
    };

    const getResultUrl = (result) => {
        switch (result.type) {
            case 'case':
                return `/case/${result.id}`;
            case 'lead':
                return `/intake/${result.id}`;
            case 'contact':
                return `/contact/${result.id}`;
            case 'document':
                return `/file/${result.id}`;
            case 'calendar':
                return `/calendar?event=${result.id}`;
            case 'feed':
                if (result.case_id) return `/case/${result.case_id}`;
                if (result.lead_id) return `/intake/${result.lead_id}`;
                return null;
            case 'field_update':
                if (result.case_id) return `/case/${result.case_id}`;
                if (result.lead_id) return `/intake/${result.lead_id}`;
                return null;
            default:
                return null;
        }
    };

    const getResultIcon = (type) => {
        switch (type) {
            case 'case':
                return <Briefcase size={16} className="result-type-icon" />;
            case 'lead':
                return <Signature size={16} className="result-type-icon" />;
            case 'contact':
                return <UserRound size={16} className="result-type-icon" />;
            case 'document':
                return <FileText size={16} className="result-type-icon" />;
            case 'calendar':
                return <Calendar size={16} className="result-type-icon" />;
            case 'feed':
                return <FileText size={16} className="result-type-icon" />;
            case 'field_update':
                return <FileText size={16} className="result-type-icon" />;
            default:
                return <FileText size={16} className="result-type-icon" />;
        }
    };

    const formatResultTitle = (result) => {
        switch (result.type) {
            case 'case':
                return result.case_name;
            case 'lead':
                return result.contact_name || `Lead ${result.id}`;
            case 'contact':
                return result.full_name || result.company_name;
            case 'document':
                return result.file_name;
            case 'calendar':
                return result.calendar_title;
            case 'feed':
                return result.subject || result.content?.substring(0, 100) + '...';
            case 'field_update':
                return `${result.field_name || 'Custom Field'} Data`;
            default:
                return result.title || result.name || 'Unknown';
        }
    };

    const formatResultSubtitle = (result) => {
        switch (result.type) {
            case 'case':
                return `Case ${result.id}${result.phase_name ? ' • ' + result.phase_name : ''}${result.first_primary ? ' • ' + result.first_primary : ''}${result.contact_name ? ' • ' + result.contact_name : ''}`;
            case 'lead':
                return `Lead ${result.id}${result.status_name ? ' • ' + result.status_name : ''}${result.case_type_name ? ' • ' + result.case_type_name : ''}`;
            case 'contact':
                return `Contact ${result.id}${result.contact_type ? ' • ' + result.contact_type : ''}${result.company_name && result.full_name ? ' • ' + result.company_name : ''}`;
            case 'document':
                return `${result.case_name || 'Document'} • ${result.folder_path?.replace("{{Name}}", result.case_name) || ''}${result.first_primary ? ' • ' + result.first_primary : ''}${result.size ? ' • ' + formatFileSize(result.size) : ''}`;
            case 'calendar':
                return `${formatDate(result.start_date)}${result.notes ? ' • ' + result.notes.substring(0, 50) + '...' : ''}${result.first_primary ? ' • ' + result.first_primary : ''}`;
            case 'feed':
                return `${result.feed_type} • ${formatDate(result.creation_date)}${result.case_name ? ' • ' + result.case_name : ''}${result.first_primary ? ' • ' + result.first_primary : ''}`;
            case 'field_update':
                return `${result.case_name || 'Case'} • Updated ${formatDate(result.updated_at)}${result.first_primary ? ' • ' + result.first_primary : ''}`;
            default:
                return '';
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className={`page-container ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
            <div className="page-header">
                <h2>Search Deeper</h2>
            </div>

            <div className='deeper-filter'>
                {/* Search Bar */}
                <div className="search-bar-container">
                    <div className="search-input-container" style={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <Search size={20} className="search-icon" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search across cases, leads, people, documents, and more..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                            style={{ 
                                color: 'var(--text-color)',
                                backgroundColor: 'transparent',
                                fontSize: '14px',
                                border: 'none',
                                outline: 'none',
                                width: '100%',
                                fontFamily: 'inherit',
                                paddingRight: searchTerm ? '32px' : '0' // Add padding when clear button is present
                            }}
                        />
                    </div>
                    
                    <button
                        className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <Filter size={16} />
                        Filters
                    </button>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="filters-panel">
                        <div className="filters-row">
                            <div className="filter-group" style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <label>Sort By:</label>
                                <select
                                    value={filters.sortBy}
                                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                                >
                                    <option value="relevance">Relevance</option>
                                    <option value="activity">Last Activity</option>
                                </select>
                            </div>

                            <div className="filter-group" style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <label>Filter By:</label>
                                <select
                                    value={filters.filterByPrimary}
                                    onChange={(e) => handleFilterChange('filterByPrimary', e.target.value)}
                                >
                                    <option value="all">All Attorneys</option>
                                    {primaryUsers.map(user => (
                                        <option key={user.id} value={user.id}>
                                            {user.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-group">
                                <button
                                    className={`action ${filters.includeArchived ? 'activated' : 'deactivated'}`}
                                    onClick={() => handleFilterChange('includeArchived', !filters.includeArchived)}
                                    type="button"
                                >
                                    Include Archived
                                </button>
                            </div>

                            <div className="filter-group">
                                <button
                                    className={`action ${filters.openInNewTab ? 'activated' : 'deactivated'}`}
                                    onClick={() => handleFilterChange('openInNewTab', !filters.openInNewTab)}
                                    type="button"
                                >
                                    Open Links in New Tab
                                </button>
                            </div>
                        </div>

                        <div className="filters-row">
                            <div className="search-types-group">
                                <label>Search In:</label>
                                <div className="search-types-grid">
                                    <button
                                        className={`action ${filters.searchTypes.cases ? 'activated' : 'deactivated'}`}
                                        onClick={() => handleFilterChange('searchTypes.cases', !filters.searchTypes.cases)}
                                        type="button"
                                    >
                                        Cases
                                    </button>
                                    <button
                                        className={`action ${filters.searchTypes.leads ? 'activated' : 'deactivated'}`}
                                        onClick={() => handleFilterChange('searchTypes.leads', !filters.searchTypes.leads)}
                                        type="button"
                                    >
                                        Leads
                                    </button>
                                    <button
                                        className={`action ${filters.searchTypes.people ? 'activated' : 'deactivated'}`}
                                        onClick={() => handleFilterChange('searchTypes.people', !filters.searchTypes.people)}
                                        type="button"
                                    >
                                        People
                                    </button>
                                    <button
                                        className={`action ${filters.searchTypes.docs ? 'activated' : 'deactivated'}`}
                                        onClick={() => handleFilterChange('searchTypes.docs', !filters.searchTypes.docs)}
                                        type="button"
                                    >
                                        Docs
                                    </button>
                                    <button
                                        className={`action ${filters.searchTypes.calendar ? 'activated' : 'deactivated'}`}
                                        onClick={() => handleFilterChange('searchTypes.calendar', !filters.searchTypes.calendar)}
                                        type="button"
                                    >
                                        Calendar Events
                                    </button>
                                    <button
                                        className={`action ${filters.searchTypes.feedUpdates ? 'activated' : 'deactivated'}`}
                                        onClick={() => handleFilterChange('searchTypes.feedUpdates', !filters.searchTypes.feedUpdates)}
                                        type="button"
                                    >
                                        Activity Feed
                                    </button>
                                    <button
                                        className={`action ${filters.searchTypes.fieldUpdates ? 'activated' : 'deactivated'}`}
                                        onClick={() => handleFilterChange('searchTypes.fieldUpdates', !filters.searchTypes.fieldUpdates)}
                                        type="button"
                                    >
                                        Other Case Details
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Search Stats */}
            {searchStats && !loading && (
                <div className="search-stats">
                    <span className="results-count">{searchStats.total_results} Results</span>
                    <span className="search-timing">({searchStats.search_time}ms)</span>
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="search-loading">
                    <Loader2 className="animate-spin" />
                    <span>Searching...</span>
                </div>
            )}

            {/* Results */}
            {!loading && results.length > 0 && (
                <div className="deeper-results">
                    {results.map((result, index) => (
                        <div
                            key={`${result.type}-${result.id}-${index}`}
                            className="deeper-result-item"
                            onClick={() => handleResultClick(result)}
                        >
                            <div className="result-main-content">
                                <div className="result-header">
                                    {getResultIcon(result.type)}
                                    <div className="result-title">{formatResultTitle(result)}</div>
                                    <div className="result-type-badge">{result.type}</div>
                                </div>
                                <div className="result-subtitle">{formatResultSubtitle(result)}</div>
                                {result.match_text && result.match_field && (
                                    <div className="result-match">
                                        <span className="match-field-label">{result.match_field}:</span>
                                        <span className="match-text" dangerouslySetInnerHTML={{ __html: result.match_text }}></span>
                                    </div>
                                )}
                            </div>
                            <div className="result-score">
                                {result.score}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* No Results */}
            {!loading && searchTerm && results.length === 0 && (
                <div className="no-results">
                    <FileText size={48} className="no-results-icon" />
                    <h3>No results found</h3>
                    <p>Try adjusting your search terms or filters</p>
                </div>
            )}

            {/* Empty State */}
            {!searchTerm && !loading && (
                <div className="empty-state">
                    <Search size={48} className="empty-state-icon" />
                    <h3>Advanced Search</h3>
                    <p>Search across all your cases, leads, documents, and more with powerful filtering options.</p>
                </div>
            )}

            {/* File Action Modal */}
            {showFileModal && selectedFile && (
                <Modal
                    title="What would you like to view?"
                    onClose={() => setShowFileModal(false)}
                >
                    <div className='modal-content-wrapper search-deep-modal'>
                        <div className="deeper-file-info">
                            <div>
                                <div className="deeper-file-name">
                                    <File size={20} />
                                    {selectedFile.file_name}
                                </div>
                                <div className="deeper-file-details subtext">
                                    {selectedFile.size && formatFileSize(selectedFile.size)}
                                    {selectedFile.case_name && selectedFile.size && ' • '}
                                    {selectedFile.case_name && (selectedFile.case_name.length > 80 
                                        ? selectedFile.case_name.substring(0, 80) + '...' 
                                        : selectedFile.case_name)}
                                </div>
                            </div>
                        </div>

                        <div className="call-it">
                            <button
                                className="action quaternary"
                                onClick={() => handleFileAction('file')}
                            >
                                <FileText size={16} />
                                View File
                            </button>
                            
                            {selectedFile.case_id && (
                                <button
                                    className="action quaternary"
                                    onClick={() => handleFileAction('case')}
                                >
                                    <Briefcase size={16} />
                                    View Case
                                </button>
                            )}
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default SearchDeeper;